import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import ChecklistOutlined from '@mui/icons-material/ChecklistOutlined';
import ClassOutlined from '@mui/icons-material/ClassOutlined';
import CreditScoreOutlined from '@mui/icons-material/CreditScoreOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import GppGoodOutlined from '@mui/icons-material/GppGoodOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import LocalGasStationOutlined from '@mui/icons-material/LocalGasStationOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import { t } from 'i18next';
import { ReactNode, useEffect } from 'react';

import { FeatureFlags } from '~constants/featureFlags';
import useAccess from '~hooks/useAccess/useAccess';
import { canViewCompanyOnboarding } from '~hooks/useCompanyOnboarding/companyOnboardingAccess';
import { canViewAnyDeletedRecords } from '~pages/Settings/DeletedRecords/permissions';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { userHasAccessToConfigurationManagement } from './Configuration/ConfigurationManagement';

interface NavItem {
  title: string;
  link: string;
  icon: ReactNode;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const conditionalNavItem = (access: boolean, navItem: NavItem) => {
  if (access) {
    return [navItem];
  }

  return [];
};

// useNavGroups builds the grouped Settings navigation based on the user's access
// level and active feature flags. Groups with no visible items are dropped so an
// empty section header never renders. It is a hook (it reads stores, feature
// flags, and runs an effect), so callers must invoke it unconditionally.
export const useNavGroups = (): NavGroup[] => {
  const { userStore } = useStores();
  const { getAccess } = useAccess();
  const hideDocumentTypesEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDocumentTypes,
  });
  const companyOnboardingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.companyOnboarding,
  });
  const routeOptimizationEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.routeOptimization,
  });

  useEffect(() => {
    getAccess();
  }, []);

  const payCommissionsEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.payCommissions,
  });
  const canViewCommissionDefaults =
    payCommissionsEnabled && userStore.getPermissions().canViewRate;

  const userAccess = userStore.getAccess();

  const userPermissions = userStore.getPermissions();

  const userCanViewAnyDeletedRecords = canViewAnyDeletedRecords(userPermissions);

  const userCanViewRouteOptimization =
    routeOptimizationEnabled && userPermissions.canEditRoutingSettings;

  const groups: NavGroup[] = [
    {
      label: t('settings.nav_groups.personal'),
      items: [
        {
          title: t('page_headings.user_profile'),
          link: routes.settings.userProfile,
          icon: <PersonOutline />,
        },
      ],
    },
    {
      label: t('settings.nav_groups.workspace'),
      items: [
        ...conditionalNavItem(
          companyOnboardingEnabled && canViewCompanyOnboarding(userStore),
          {
            title: t('company_onboarding.title'),
            link: routes.settings.companyOnboarding,
            icon: <ChecklistOutlined />,
          },
        ),
        ...conditionalNavItem(userAccess.manageCompanies, {
          title: t('page_headings.general_settings'),
          link: routes.settings.generalSettings,
          icon: <TuneOutlined />,
        }),
        ...conditionalNavItem(userHasAccessToConfigurationManagement(userStore), {
          title: t('administration.configuration.title'),
          link: routes.settings.configuration,
          icon: <SettingsOutlined />,
        }),
        ...conditionalNavItem(userCanViewRouteOptimization, {
          title: t('administration.route_optimization.title'),
          link: routes.settings.routeOptimization,
          icon: <RouteOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageBilling, {
          title: t('billing.tab.title'),
          link: routes.settings.billing,
          icon: <PaymentsOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageLabels, {
          title: t('administration.labels.title'),
          link: routes.settings.labelsManagement,
          icon: <LabelOutlined />,
        }),
        ...(!hideDocumentTypesEnabled
          ? conditionalNavItem(userAccess.manageCompliance, {
              title: t('administration.compliance_document_types.title'),
              link: routes.settings.complianceDocumentTypes,
              icon: <GppGoodOutlined />,
            })
          : []),
        ...conditionalNavItem(userCanViewAnyDeletedRecords, {
          title: t('settings.deleted_records.title'),
          link: routes.settings.deletedRecords,
          icon: <DeleteOutlineOutlined />,
        }),
      ],
    },
    {
      label: t('settings.nav_groups.accounts'),
      items: [
        ...conditionalNavItem(userAccess.manageUser, {
          title: t('settings.nav_items.users'),
          link: routes.settings.userManagement,
          icon: <GroupOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageUser, {
          title: t('page_headings.drivers'),
          link: routes.settings.drivers,
          icon: <BadgeOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageAccounts, {
          title: t('page_headings.customers'),
          link: routes.settings.customers,
          icon: <BusinessOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageAccounts, {
          title: t('page_headings.vendors'),
          link: routes.settings.vendors,
          icon: <StorefrontOutlined />,
        }),
      ],
    },
    {
      label: t('settings.nav_groups.ai_integrations'),
      items: [
        {
          title: t('administration.agents.title'),
          link: routes.settings.agents,
          icon: <SmartToyOutlined />,
        },
        ...conditionalNavItem(userAccess.manageTelematics, {
          title: t('administration.integration.title'),
          link: routes.settings.integrations.base,
          icon: <HubOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageM2MClients, {
          title: t('settings.nav_items.api_creds'),
          link: routes.settings.apiClients,
          icon: <VpnKeyOutlined />,
        }),
      ],
    },
    {
      label: t('settings.nav_groups.data_setup'),
      items: [
        ...conditionalNavItem(userAccess.manageSites, {
          title: t('administration.site.title'),
          link: routes.settings.sitesManagement,
          icon: <PlaceOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageEquipment, {
          title: t('administration.equipment_type.title'),
          link: routes.settings.equipmentTypeManagement,
          icon: <CategoryOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageEquipment, {
          title: t('administration.equipment.title'),
          link: routes.settings.equipmentManagement,
          icon: <LocalShippingOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageMaterials, {
          title: t('administration.material_type.title'),
          link: routes.settings.materialTypeManagement,
          icon: <LayersOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageMaterials, {
          title: t('administration.material.title'),
          link: routes.settings.materialsManagement,
          icon: <InventoryOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageDepartments, {
          title: t('administration.department.title'),
          link: routes.settings.departmentsManagement,
          icon: <AccountTreeOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageServiceClasses, {
          title: t('administration.service_class.title'),
          link: routes.settings.serviceClassesManagement,
          icon: <ClassOutlined />,
        }),
      ],
    },
    {
      label: t('settings.nav_groups.rates'),
      items: [
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('settings.nav_items.freight_rates'),
          link: routes.settings.ratesManagement,
          icon: <RequestQuoteOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('administration.material_rates.title'),
          link: routes.settings.materialRatesManagement,
          icon: <SellOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('settings.nav_items.add_ons'),
          link: routes.settings.addOnCharges,
          icon: <AddCircleOutline />,
        }),
        ...conditionalNavItem(canViewCommissionDefaults, {
          title: t('pay_commission_defaults.title'),
          link: routes.settings.commissionDefaults,
          icon: <AccountBalanceOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('settings.nav_items.recurring_settlement_add_ons'),
          link: routes.settings.automaticSettlementAddOnManagement,
          icon: <AccountBalanceOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('administration.payment_terms.title'),
          link: routes.settings.paymentTermsManagement,
          icon: <CreditScoreOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('settings.nav_items.fuel_surcharge'),
          link: routes.settings.fuelSurchargeManagement,
          icon: <LocalGasStationOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageRates, {
          title: t('administration.tax_rate_schedule.title'),
          link: routes.settings.taxRateScheduleManagement,
          icon: <ReceiptLongOutlined />,
        }),
        ...conditionalNavItem(userAccess.manageOvertimeRules, {
          title: t('settings.nav_items.ot_rules'),
          link: routes.settings.overtimeRulesManagement,
          icon: <AccessTimeOutlined />,
        }),
        ...conditionalNavItem(userAccess.managePrevailingWageBenefits, {
          title: t('administration.prevailing_wage_benefits.title'),
          link: routes.settings.prevailingWageBenefitsManagement,
          icon: <GavelOutlined />,
        }),
      ],
    },
  ];

  return groups.filter((group) => group.items.length > 0);
};
